import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  LoginRequest, LoginResponse, UsuarioInfo, ApiResponse, RefreshTokenRequest
} from '../../models/auth.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly API = `${environment.apiUrl}/auth`;
  private readonly TOKEN_KEY = 'access_token';
  private readonly USER_KEY = 'user_info';

  // Signals para estado reactivo
  private _usuario = signal<UsuarioInfo | null>(this.loadUserFromStorage());
  private _isLoading = signal(false);

  // Computed signals públicos
  readonly usuario = this._usuario.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly isAuthenticated = computed(() => this._usuario() !== null && !this.isTokenExpired());
  readonly roles = computed(() => this._usuario()?.roles ?? []);
  readonly empresaId = computed(() => this._usuario()?.empresaId ?? 0);
  readonly empresas = computed(() => this._usuario()?.empresas ?? []);
  readonly onboardingCompletado = computed(() => this._usuario()?.onboardingCompletado ?? true);
  readonly sectorNegocioNombre = computed(() => this._usuario()?.sectorNegocioNombre ?? null);
  readonly modulosHabilitados = computed(() => this._usuario()?.modulosHabilitados ?? null);

  markOnboardingComplete(): void {
    const u = this._usuario();
    if (!u) return;
    const updated = { ...u, onboardingCompletado: true };
    localStorage.setItem(this.USER_KEY, JSON.stringify(updated));
    this._usuario.set(updated);
  }

  constructor(private http: HttpClient, private router: Router) {}

  login(request: LoginRequest): Observable<ApiResponse<LoginResponse>> {
    this._isLoading.set(true);
    return this.http.post<ApiResponse<LoginResponse>>(`${this.API}/login`, request).pipe(
      tap(response => {
        if (response.success) {
          this.saveSession(response.data);
        }
        this._isLoading.set(false);
      }),
      catchError(error => {
        this._isLoading.set(false);
        return throwError(() => error);
      })
    );
  }

  refreshToken(): Observable<ApiResponse<LoginResponse>> {
    const refreshToken = this.getRefreshToken();
    return this.http.post<ApiResponse<LoginResponse>>(
      `${this.API}/refresh-token`,
      { token: refreshToken } as RefreshTokenRequest
    ).pipe(
      tap(response => {
        if (response.success) {
          this.saveSession(response.data);
        }
      })
    );
  }

  logout(): void {
    const refreshToken = this.getRefreshToken();
    this.http.post(`${this.API}/logout`, { refreshToken }).subscribe();
    this.clearSession();
    this.router.navigate(['/auth/login']);
  }

  cambiarEmpresa(empresaId: number): void {
    // Requiere nuevo login para obtener token de la empresa seleccionada
    const usuarioActual = this._usuario();
    if (!usuarioActual) return;
    this.clearSession();
    this.router.navigate(['/auth/login'], { queryParams: { empresaId } });
  }

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  hasAnyRole(...roles: string[]): boolean {
    return roles.some(r => this.hasRole(r));
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }

  getTokenExpirationDate(): Date | null {
    const token = this.getAccessToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return new Date(payload.exp * 1000);
    } catch {
      return null;
    }
  }

  private saveSession(data: LoginResponse): void {
    localStorage.setItem(this.TOKEN_KEY, data.accessToken);
    localStorage.setItem('refresh_token', data.refreshToken);
    localStorage.setItem(this.USER_KEY, JSON.stringify(data.usuario));
    this._usuario.set(data.usuario);
  }

  private clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem('refresh_token');
    localStorage.removeItem(this.USER_KEY);
    this._usuario.set(null);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  private loadUserFromStorage(): UsuarioInfo | null {
    try {
      const raw = localStorage.getItem(this.USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}
